// pages/Campaigns/Gmail/ListGmailEmails.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import backendURL from "../../../config";
import { Tooltip, Modal as MuiModal, Box, IconButton, Stack, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ReplyModal from "./ReplyModal";
import PromptSettings from "./PromptSettings";

export default function ListGmailEmails({ refreshTrigger }) {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [infoMessage, setInfoMessage] = useState('');
    const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isBulkReplying, setIsBulkReplying] = useState(false);
    const [isTraining, setIsTraining] = useState(false);
    const [isBulkTraining, setIsBulkTraining] = useState(false);
    
    const [trainingStatusFilter, setTrainingStatusFilter] = useState('all');
    const [emailStatusFilter, setEmailStatusFilter] = useState('all');

    const fetchEmails = useCallback(async (showFeedback = false) => {
        setLoading(true);
        setError(null);
        if (showFeedback) {
            setInfoMessage('');
        }
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(`${backendURL}/api/gmail/check-emails`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Manual-Fetch': 'true'
                },
            });
            setEmails(response.data.data || []);
            if (showFeedback && response.data.message) {
                setInfoMessage(response.data.message);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Error fetching emails.");
            setInfoMessage('');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEmails(false);
    }, [refreshTrigger, fetchEmails]);

    const handleManualFetch = () => fetchEmails(true);

    const handleOpenReplyModal = useCallback((email) => {
        setSelectedEmail(email);
        setIsReplyModalOpen(true);
    }, []);

    const handleCloseReplyModal = useCallback((shouldRefresh = false) => {
        setIsReplyModalOpen(false);
        setSelectedEmail(null);
        if (shouldRefresh) {
            fetchEmails(true);
        }
    }, [fetchEmails]);

    const handleTrainEmail = useCallback(async (email) => {
        setIsTraining(true);
        setInfoMessage(`Training AI with email: ${email.subject}...`);
        setError('');
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(`${backendURL}/api/gmail/train-reply`,
                { message_db_id: email.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInfoMessage(response.data.message);
            await fetchEmails(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to train email.');
        } finally {
            setIsTraining(false);
        }
    }, [fetchEmails]);

    const handleUntrainEmail = useCallback(async (email) => {
        setIsTraining(true);
        setInfoMessage(`Removing training for email: ${email.subject}...`);
        setError('');
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(`${backendURL}/api/gmail/untrain-reply`,
                { message_db_id: email.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInfoMessage(response.data.message);
            await fetchEmails(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to untrain email.');
        } finally {
            setIsTraining(false);
        }
    }, [fetchEmails]);

    const handleBulkTrain = async () => {
        if (selectedRows.length === 0) return;
        setIsBulkTraining(true);
        const validRowsToTrain = selectedRows.filter(row => !row.is_trained && (row.status === 'replied_ai' || row.status === 'replied_manual'));
        const message_db_ids = validRowsToTrain.map(row => row.id);

        if (message_db_ids.length === 0) {
            setInfoMessage("No valid, untrained emails selected for training.");
            setIsBulkTraining(false);
            return;
        }

        setInfoMessage(`Training AI with ${message_db_ids.length} emails...`);
        setError('');
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(`${backendURL}/api/gmail/bulk-train`,
                { message_db_ids },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInfoMessage(response.data.message);
            setSelectedRows([]);
            await fetchEmails(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to perform bulk training.');
        } finally {
            setIsBulkTraining(false);
        }
    };
    
    const handleBulkReply = async () => {
        if (selectedRows.length === 0) return;
        setIsBulkReplying(true);
        const validRowsToReply = selectedRows.filter(row => row.status === 'reply_generated');
        const message_db_ids = validRowsToReply.map(row => row.id);

        if (message_db_ids.length === 0) {
            setInfoMessage("No valid emails selected for reply.");
            setIsBulkReplying(false);
            return;
        }

        setInfoMessage(`Sending ${message_db_ids.length} replies...`);
        setError('');
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(`${backendURL}/api/gmail/bulk-reply`, 
                { message_db_ids },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInfoMessage(response.data.message || "Bulk reply process completed.");
            setSelectedRows([]);
            await fetchEmails(false);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to send bulk replies.");
        } finally {
            setIsBulkReplying(false);
        }
    };
    
    const filteredEmails = useMemo(() => {
        return emails.filter(email => {
            const trainingMatch =
                trainingStatusFilter === 'all' ||
                (trainingStatusFilter === 'trained' && email.is_trained) ||
                (trainingStatusFilter === 'untrained' && !email.is_trained);

            const statusMatch =
                emailStatusFilter === 'all' ||
                email.status === emailStatusFilter;

            return trainingMatch && statusMatch;
        });
    }, [emails, trainingStatusFilter, emailStatusFilter]);


    const columns = useMemo(() => [
        { name: "From", selector: (row) => row.from_address, sortable: true, grow: 2, wrap: true },
        { name: "Subject", selector: (row) => row.subject, sortable: true, grow: 3, wrap: true },
        { name: "Received", selector: (row) => new Date(row.received_at).toLocaleString(), sortable: true, grow: 2 },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => {
                let badgeClass = 'badge ';
                let text = row.status.replace(/_/g, ' ').toUpperCase();

                if (row.is_trained) {
                    badgeClass += 'bg-primary';
                    text += ' (TRAINED)';
                } else {
                    switch (row.status) {
                        case 'replied_manual': case 'replied_ai': badgeClass += 'bg-success'; break;
                        case 'reply_generated': badgeClass += 'bg-info'; break;
                        case 'processing': badgeClass += 'bg-warning'; break;
                        case 'error': badgeClass += 'bg-danger'; break;
                        default: badgeClass += 'bg-secondary';
                    }
                }
                return <span className={badgeClass}>{text}</span>;
            }
        },
        {
            name: "Actions",
            cell: (row) => {
                const isReplied = row.status === 'replied_manual' || row.status === 'replied_ai';
                const commonDisabled = loading || isBulkReplying || isTraining || isBulkTraining;

                return (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Email & Reply" arrow>
                            <span>
                                <IconButton size="small" color="info" onClick={() => handleOpenReplyModal(row)} disabled={commonDisabled}>
                                    <i className="fa fa-eye"></i>
                                </IconButton>
                            </span>
                        </Tooltip>
                        {isReplied && (
                            row.is_trained ? (
                                <Tooltip title="Remove this reply from AI's knowledge" arrow>
                                    <span>
                                        <IconButton size="small" color="error" onClick={() => handleUntrainEmail(row)} disabled={commonDisabled}>
                                            <i className="fa fa-unlink"></i>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Train AI with this reply" arrow>
                                    <span>
                                        <IconButton size="small" color="primary" onClick={() => handleTrainEmail(row)} disabled={commonDisabled}>
                                            <i className="fa fa-magic"></i>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                        )}
                    </Stack>
                )
            },
        },
    ], [loading, isBulkReplying, isTraining, isBulkTraining, handleOpenReplyModal, handleTrainEmail, handleUntrainEmail]);

    const selectableRowDisabled = row => {
        const isSelectable = row.status === 'reply_generated' || row.status === 'replied_ai' || row.status === 'replied_manual';
        return !isSelectable;
    };

    const settingsModalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '80%', md: '60vw' }, maxWidth: '800px',
        maxHeight: '90vh', bgcolor: 'background.paper',
        border: 'none', borderRadius: '8px', boxShadow: 24, p: 3,
        display: 'flex', flexDirection: 'column', outline: 'none',
    };

    const selectedForReply = selectedRows.filter(r => r.status === 'reply_generated').length;
    const selectedForTrain = selectedRows.filter(r => (r.status === 'replied_ai' || r.status === 'replied_manual') && !r.is_trained).length;

    return (
        <div>
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={() => setIsSettingsModalOpen(true)} disabled={loading || isBulkReplying || isBulkTraining}>
                    <i className="fa fa-cog me-1"></i> Settings
                </Button>
                <Button variant="contained" color="primary" onClick={handleBulkTrain} disabled={selectedForTrain === 0 || loading || isBulkReplying || isBulkTraining}>
                    {isBulkTraining ? <CircularProgress size={20} color="inherit" /> : <i className="fa fa-robot me-1"></i>}
                    Bulk Train ({selectedForTrain})
                </Button>
                <Button variant="contained" color="success" onClick={handleBulkReply} disabled={selectedForReply === 0 || loading || isBulkReplying || isBulkTraining}>
                    {isBulkReplying ? <CircularProgress size={20} color="inherit" /> : <i className="fa fa-paper-plane me-1"></i>}
                    Bulk Reply ({selectedForReply})
                </Button>
                <Button variant="contained" color="secondary" onClick={handleManualFetch} disabled={loading || isBulkReplying || isBulkTraining}>
                    {loading && !isBulkReplying && !isBulkTraining ? <CircularProgress size={20} color="inherit" /> : <i className="fa fa-refresh me-1"></i>}
                    Check for New Emails
                </Button>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <FormControl fullWidth size="small">
                    <InputLabel id="training-status-filter-label">Training Status</InputLabel>
                    <Select
                        labelId="training-status-filter-label"
                        label="Training Status"
                        value={trainingStatusFilter}
                        onChange={(e) => setTrainingStatusFilter(e.target.value)}
                    >
                        <MenuItem value="all">All Training Statuses</MenuItem>
                        <MenuItem value="trained">Trained</MenuItem>
                        <MenuItem value="untrained">Untrained</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                    <InputLabel id="email-status-filter-label">Email Status</InputLabel>
                    <Select
                        labelId="email-status-filter-label"
                        label="Email Status"
                        value={emailStatusFilter}
                        onChange={(e) => setEmailStatusFilter(e.target.value)}
                    >
                        <MenuItem value="all">All Email Statuses</MenuItem>
                        <MenuItem value="replied_ai">Replied (AI)</MenuItem>
                        <MenuItem value="replied_manual">Replied (Manual)</MenuItem>
                        <MenuItem value="reply_generated">Reply Generated</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                        <MenuItem value="error">Error</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {error && <div className="alert alert-danger mt-2">{error}</div>}
            {infoMessage && <div className="alert alert-info mt-2">{infoMessage}</div>}
            
            <DataTable
                columns={columns}
                data={filteredEmails}
                progressPending={loading}
                pagination
                responsive
                highlightOnHover
                selectableRows
                onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                selectableRowDisabled={selectableRowDisabled}
                clearSelectedRows={isBulkReplying || isBulkTraining}
            />
            {isReplyModalOpen && (
                <ReplyModal
                    isOpen={isReplyModalOpen}
                    onClose={handleCloseReplyModal}
                    emailData={selectedEmail}
                    onTrain={handleTrainEmail}
                    onUntrain={handleUntrainEmail}
                />
            )}
            <MuiModal open={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)}>
                <Box sx={settingsModalStyle}>
                    <h4 className="mb-3">AI Prompt Settings</h4>
                    <PromptSettings />
                    <div className="d-flex justify-content-end mt-3">
                        <Button variant="outlined" onClick={() => setIsSettingsModalOpen(false)}>Close</Button>
                    </div>
                </Box>
            </MuiModal>
        </div>
    );
}